import React from 'react';
import { TrendingUp, Target, AlertTriangle } from 'lucide-react';
import { Modal } from '../Modal';
import { Card } from '../Card';
import { Tooltip } from '../Tooltip';
import { cn } from '../../../utils/cn';
import { StockCategoryFilter, CategoryHelpers } from '../../../types/stockCategories';
import { useTutorial } from '../../../contexts/TutorialContext';
import { useGameStart } from '../../../contexts/GameStartContext';

export interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  category?: StockCategoryFilter;
  showCategoryContext?: boolean;
}

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  className?: string;
}

const Section: React.FC<SectionProps> = ({ icon, title, children, className }) => (
  <Card className={cn('p-4', className)}>
    <div className="flex items-center gap-2 mb-3">
      <div className="text-primary">{icon}</div>
      <h3 className="text-lg font-semibold text-foreground">{title}</h3>
    </div>
    <div className="space-y-2 text-sm text-muted-foreground">
      {children}
    </div>
  </Card>
);

interface RuleItemProps {
  children: React.ReactNode;
}

const RuleItem: React.FC<RuleItemProps> = ({ children }) => (
  <div className="flex items-start gap-2">
    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
    <p className="leading-relaxed">{children}</p>
  </div>
);

export const HelpModal: React.FC<HelpModalProps> = ({
  isOpen,
  onClose,
  className,
  category,
  showCategoryContext = true
}) => {
  // Get category configuration for display
  const categoryConfig = category ? CategoryHelpers.getConfig(category) : null;
  const { startTutorial } = useTutorial();
  const { hasGameStarted } = useGameStart();
  const activeCategoryConfig = showCategoryContext && categoryConfig ? categoryConfig : null;

  const titleText = activeCategoryConfig
    ? `How to Play ${activeCategoryConfig.label} Wallstreetle`
    : 'How to Play Wallstreetle';

  const descriptionText = activeCategoryConfig
    ? `Learn the rules and strategies for the daily ${activeCategoryConfig.label.toLowerCase()} stock guessing game`
    : 'Learn the rules and strategies for the daily stock guessing game';

  const handleTitleClick = React.useCallback(() => {
    startTutorial();
    onClose();
  }, [startTutorial, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center justify-between gap-3 w-full">
          <span>{titleText}</span>
          {!hasGameStarted ? (
            <Tooltip content="Tutorial can only start once game has begun" position="bottom">
              <button
                type="button"
                disabled
                className="shrink-0 text-sm font-medium px-4 py-2 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              >
                Start Tutorial
              </button>
            </Tooltip>
          ) : (
            <button
              type="button"
              onClick={handleTitleClick}
              className="shrink-0 text-sm font-medium px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
            >
              Start Tutorial
            </button>
          )}
        </div>
      }
      description={descriptionText}
      headerClassName="p-4 sm:p-6 pb-3 sm:pb-4 !items-start"
      className={cn('max-w-2xl max-h-[90vh] md:max-h-[95vh] overflow-y-auto', className)}
    >
      <div className="space-y-6">
        {/* Category Badge (if available) */}
        {showCategoryContext && categoryConfig && (
          <div className="flex justify-center">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white shadow-sm"
              style={{ backgroundColor: categoryConfig.color }}
            >
              <span role="img" aria-label={categoryConfig.label}>
                {categoryConfig.icon}
              </span>
              <span>{categoryConfig.label} Category</span>
            </div>
          </div>
        )}

        {/* About Section */}
        <Section
          icon={<TrendingUp className="h-5 w-5" />}
          title={showCategoryContext && categoryConfig ? `About ${categoryConfig.label} Mode` : "About the Game"}
        >
          <RuleItem>
            <strong>Wallstreetle</strong> is a daily stock-guessing game inspired by Wordle,
            designed for finance enthusiasts and anyone curious about the stock market.
          </RuleItem>
          <RuleItem>
            {showCategoryContext && categoryConfig
              ? `Each day features a new ${categoryConfig.label.toLowerCase()} company for you to identify using financial clues and market data. ${categoryConfig.description}`
              : 'Each day features a new publicly traded company for various categories for you to identify using financial clues and market data.'
            }
          </RuleItem>
          <RuleItem>
            {showCategoryContext && categoryConfig
              ? `It's a fun way to learn about ${categoryConfig.label.toLowerCase()} companies, practice financial analysis, and test your market knowledge in this specialized category.`
              : "It's a fun way to learn about different companies, practice financial analysis, and test your market knowledge."
            }
          </RuleItem>
        </Section>

        {/* Game Rules Section */}
        <Section
          icon={<Target className="h-5 w-5" />}
          title="Game Rules"
        >
          <RuleItem>
            You have <strong>4 attempts</strong> to guess the correct stock ticker symbol.
          </RuleItem>
          <RuleItem>
            Type the company name or stock ticker in the Submit Guess section to lock in your answer.
          </RuleItem>
          <RuleItem>
            Each incorrect guess reveals a new hint about the company, including industry position,
            public recognition, leadership, and products & services.
          </RuleItem>
          <RuleItem>
            All categories update daily with a new stock challenge.
          </RuleItem>
        </Section>

        {/* Disclaimer Section */}
        <Section
          icon={<AlertTriangle className="h-5 w-5" />}
          title="Important Disclaimer"
          className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20"
        >
          <RuleItem>
            Wallstreetle is for entertainment and educational purposes only
            and does not constitute financial advice.
          </RuleItem>
          <RuleItem>
            Always consult a licensed financial advisor for investment guidance specific
            to your personal situation.
          </RuleItem>
          <RuleItem>
            Data accuracy is not guaranteed and cannot be relied on to make investment decisions.
          </RuleItem>
          <RuleItem>
            Always conduct your own research before making investment decisions.
          </RuleItem>
        </Section>
      </div>
    </Modal>
  );
};

export default HelpModal;
